import React, { useState } from 'react'
import { FlexColumn } from '@/atoms/FlexContainers'
import { Image } from '@/atoms/Image'
import { CaptionSM, Text, TitleXS, TitleXXS, HeadingXS } from '@/atoms/Text'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { FlexibleBookingToolTip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/FlexibleBookingToolTip'
import { SpecificTimesToolTip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/SpecificTimesToolTip'
import {
  NoFeesVales,
  NoFeesValuesType,
} from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/config/NoFeesValues'

const BANNER_CONFIGS = {
  desktop: {
    containerStyles: 'absolute inset-x-0 bottom-0 p-0 @container/banner',
    wrapperStyles:
      'text-[#4d3a86] @xs/banner:ml-[3%] @xs/banner:pb-[3%] @lg/banner:ml-[0] @lg/banner:pb-[0] @xl/banner:ml-[0.5%] @xl/banner:pb-[0.5%] @3xl/banner:ml-[1%] @3xl/banner:pb-[1%] @4xl/banner:ml-[0.5%] @4xl/banner:pb-[0.5%] @7xl/banner:ml-[1%] @7xl/banner:pb-[1%]',
    buttonStyles: `!text-[#4d3a86] text-xs @lg/banner:text-[10px] @2xl/banner:text-sm @3xl/banner:text-md @5xl/banner:text-lg @7xl/banner:text-xl !ring-0 !ring-transparent`,
    imageSize: {
      height: 334,
      width: 2000,
    },
    videoStyles: 'z-20 right-0',
  },
  tablet: {
    containerStyles: 'absolute inset-x-0 bottom-0 p-0 @container/banner',
    wrapperStyles: 'ml-[2%] pb-[2%] text-[#4d3a86] @xl/banner:ml-[2%] @xl/banner:pb-[2%]',
    buttonStyles: `!text-[#4d3a86] text-base !ring-0 !ring-transparent`,
    imageSize: {
      height: 300,
      width: 1200,
    },
    videoStyles: 'z-10 -right-[2em]',
  },
  mobile: {
    containerStyles: 'absolute inset-x-0 bottom-0 p-0 @container/banner',
    wrapperStyles: 'ml-[1%] pb-[3%] text-[#4d3a86]',
    buttonStyles: `text-[11px] @md/banner:text-sm !ring-0 !ring-transparent !text-[#4d3a86]`,
    imageSize: {
      height: 286,
      width: 856,
    },
    videoStyles: 'z-10 -right-[4em]',
  },
}

const CreateOverlay = (size: 'desktop' | 'tablet' | 'mobile', bannerValues: NoFeesValuesType, code: string) => {
  const config = BANNER_CONFIGS[size]
  const { t } = useTranslate('tickets')
  const [videoFailed, setVideoFailed] = useState(false)

  const handleVideoError = () => {
    setVideoFailed(true)
  }
  return (
    <div className={`relative mb-2 rounded-xl bg-[#CDE4E9]`}>
      <div className=" relative h-full min-h-[160px] w-full rounded-xl @container/banner  @xl:!min-h-[140px]">
        <div
          className={'relative hidden h-full min-h-[160px] overflow-hidden rounded-r-xl @md:block @xl:!min-h-[140px]'}
        >
          {!videoFailed && bannerValues.video ? (
            <video
              preload="auto"
              className={`${config.videoStyles} absolute -top-[5%] right-0 h-[110%] w-auto rounded-xl object-contain `}
              autoPlay
              loop
              muted
              playsInline
              onError={handleVideoError}
              src={bannerValues.video}
            />
          ) : (
            <Image
              src={bannerValues.image || ''}
              className={`${config.videoStyles} absolute !-right-[1.5em] top-[30%] h-[70%] w-auto rounded-xl object-contain  pr-1 @md:top-0 @md:h-[100%] @lg:-top-[5%] @lg:h-[110%] @xl:!right-0 @4xl:pr-6 `}
              width={200}
              height={200}
              alt={''}
            />
          )}
        </div>
        {/* Main content container - absolute positioning to fill parent */}
        <div className="absolute inset-0 z-10 flex w-[100%] items-stretch rounded-xl px-4 @container/content @md:w-[74%] @md:px-0">
          {/*LEFT TEXT*/}
          <FlexColumn className="flex w-[100%]  justify-center py-1   @sm/content:w-[60%] @sm:flex-1 @sm:pl-4 @md/content:w-[40%] @md:w-[37%] @md:px-4 @4xl:px-6  @4xl:py-3">
            <div className={'w-[165px] @md:w-[200px] @lg:w-[100%]'}>
              <HeadingXS
                className=" relative text-xl leading-6 @md:!text-2xl @md:leading-7 "
                weight="bold"
                color="black"
              >
                <Translate i18nKey="noOnlineFeesNoHassle" t={t}>
                  No Online Fees. <span>No Hassle.</span>
                </Translate>
              </HeadingXS>
              {/*TOOLTIP*/}
              <div style={{ zIndex: 70 }}>
                <SpecificTimesToolTip buttonClassName="!px-0" buttonColor={'black'}>
                  <TitleXS color="black" className="hidden @xl:block">
                    {t('onSelectShowtimes', 'On Select Showtimes')}
                  </TitleXS>
                  <TitleXXS color="black" className="@xl:hidden">
                    {t('onSelectShowtimes', 'On Select Showtimes')}
                  </TitleXXS>
                </SpecificTimesToolTip>
                <FlexibleBookingToolTip
                  className="mt-1"
                  buttonColor={'black'}
                  buttonClassName="bg-black/10 border-none text-black focus-visible:outline-none focus-visible:ring-offset-transparent focus-visible:ring-transparent backdrop-blur-[24px] !backdrop-filter"
                />
              </div>
            </div>
          </FlexColumn>
          {/*{BUTTON}*/}
          <FlexColumn className="flex items-start justify-center  @sm:flex-1  @md:px-3 @xl/content:flex-1  @4xl:px-6 @4xl:py-3">
            <div
              className={
                'text-center @sm:w-[150px] @md/content:w-[200px] @md/content:text-left @xl/content:w-[100%] @xl/content:text-center'
              }
            >
              <Text className="photon-eyebrow-sm @md/content:!photon-eyebrow-md text-black" weight="semibold">
                {t('usePromoCode', 'Use Promo Code')}
              </Text>
              <div className={'my-2 justify-center'}>
                <span className=" bg-[#EA3358] px-3 py-2 font-bold text-white">{code || 'NOFEES'}</span>
              </div>
              <Text className="photon-eyebrow-sm @md/content:!photon-eyebrow-md text-black " weight="semibold">
                {t('atCheckout', 'At Checkout')}
              </Text>
              <CaptionSM
                color="core-gray-500"
                italic
                className="mt-1 text-center @md:text-left @xl/content:mt-0 @xl/content:text-center"
              >
                {t('angelPartneringTheaters', 'Available for Angel Partnering Theaters.')}
              </CaptionSM>
            </div>
          </FlexColumn>
        </div>
      </div>
    </div>
  )
}

export const NoFeesBanner = ({ theatricalSlug, code }: { theatricalSlug: string; code: string }) => {
  const bannerValues = NoFeesVales[theatricalSlug]

  return (
    <div className="relative z-[39] mb-2 min-h-[160px] @container @md:min-h-[140px]">
      <div className="relative hidden lg:block">{CreateOverlay('desktop', bannerValues, code)}</div>
      <div className="relative hidden min-h-[160px] md:block lg:hidden">
        {CreateOverlay('tablet', bannerValues, code)}
      </div>
      <div className="relative min-h-[160px] md:hidden">{CreateOverlay('mobile', bannerValues, code)}</div>
    </div>
  )
}
